import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import _ from 'lodash';
import { createStructuredSelector } from 'reselect';
import businessProducts from '../../utils/products';
import { makeSelectReady, makeSelectFetching, makeSelectSims, makeSelectCurrentOrder, makeSelectNonActivatedSims, makeSelectNonActivatedSimsFetching } from '../../modules/sims/selectors';
import * as simActions from '../../modules/sims/actions';
import { makeSelectFetchingUsage, makeSelectAllUsages } from '../../modules/usage/selectors';
import { makeSelectActiveBillingPeriod, makeSelectBillingPeriods } from '../../modules/billing/selectors';
import { makeSelectEmptySims } from '../../modules/emptySims/selectors';
import { initializeAllUsages, fetchAllUsages } from '../../modules/usage/actions';
import { setActiveBillingPeriod } from '../../modules/billing/actions';
import { fetchEmptySims } from '../../modules/emptySims/actions';
import { getAccountId, getUIString } from '../../utils/helpers';
import { selectErrors } from '../../modules/global/selectors';
import { DashboardContent } from './DashboardContent';
import { OfferingType } from '../../utils/offeringType';

// TODO more specific parameter types
interface Props {
	usage: any;
	activeBillingPeriod: any;
	currentOrder: any;
	emptySims: any;
	sims: any[];
	accountType: string;
	nonActivatedSims: any[];
	usageFetching: boolean;
	billingPeriods: any[];
	errors: any;
	nonActivatedSimsFetching: boolean;
	ready: boolean;
	simsfetching: boolean;
	offeringType: OfferingType;
}

export const DashboardPageImpl: React.FC<Props> = (props: Props) => {
	const dispatch = useDispatch();

	useEffect(() => {
		const { emptySims, usage, activeBillingPeriod, usageFetching } = props;
		const accountId = getAccountId();

		dispatch(simActions.fetchSims(accountId));
		if (_.isEmpty(usage) && _.isEmpty(activeBillingPeriod) && !usageFetching) {
			dispatch(initializeAllUsages(accountId));
		} else if (activeBillingPeriod && !usageFetching) {
			dispatch(fetchAllUsages(accountId, activeBillingPeriod.startDate, activeBillingPeriod.endDate));
		}
		if (_.isEmpty(currentOrder)) {
			dispatch(simActions.fetchCurrentOrder(accountId));
		}
		if (_.isEmpty(emptySims)) {
			dispatch(fetchEmptySims(accountId));
		}
		if (accountType === 'consumer') {
			dispatch(simActions.fetchNonActivatedSims(accountId));
		}
	}, []); // eslint-disable-line

	const getMaxValue = (packageSize: any, type = 'data') => {
		if (packageSize === 0) {
			return type === 'data' ? 10 : 300;
		}
		return getDataPerSimNumber(packageSize) * 2;
	};

	const getMessageAndVoiceUsage = (usage: any) => {
		if (usage) {
			return parseInt(usage.sumOverAllMoisUsage, 10);
		}
		return 0;
	};

	const getDataUsage = (usage: any, isOngoingBillingPeriod: boolean) => {
		if (usage) {
			if (isOngoingBillingPeriod) {
				return parseFloat(usage.sumDataUsageExcludingUnlimited).toFixed(2);
			}
			return parseFloat(usage.sumOverAllDataUsage).toFixed(2);
		}
		return null;
	};

	const getDataPerSimNumber = (dataPerSim: any) => {
		if (dataPerSim === Infinity || dataPerSim === 'Rajaton data' || dataPerSim === 'unlimited') {
			return Infinity;
		}
		if (_.isNumber(Number(dataPerSim)) === false) {
			return 0;
		}
		return Number(String(dataPerSim).replace(/gt/gi, ''));
	};

	// Todo: move to utils/products.js
	const getPrice = (moisPerSim: any, dataPerSim: any) => {
		// const voice = currentOrder.moisPerSim;
		// const dataPackages = ['5gt', '10gt', 'Rajaton data'];
		// const currentOrder = this.props.currentOrder;
		const data = dataPerSim === 'unlimited' ? 'Rajaton data' : dataPerSim;
		const prices = [
			{ '5gt': 5, '10gt': 8, 'Rajaton data': 15 }, // voice 0
			{ '5gt': 11, '10gt': 14, 'Rajaton data': 19 }, // voice 300
			{ '5gt': 14, '10gt': 17, 'Rajaton data': 21 }, // voice 600
			{ '5gt': 19, '10gt': 21, 'Rajaton data': 24 } // voice 1200
		];
		const voices = [0, 300, 600, 1200];

		for (let i = 0; i < voices.length; i += 1) {
			if (voices[i] === moisPerSim) {
				const arr = prices[i];
				// @ts-ignore
				return arr[data];
			}
		}
		return null;
	};

	const updateBillingPeriod = (billingPeriod: any) => {
		dispatch(setActiveBillingPeriod(billingPeriod));
		dispatch(fetchAllUsages(getAccountId(), billingPeriod.startDate, billingPeriod.endDate));
	};

	const { sims, currentOrder, accountType, nonActivatedSims } = props;
	let simsCount = 0;
	let moisPerSim = 0;
	let moisTotal = 0;
	let dataPerSim = 0;
	let dataTotal = 0;
	let price = null;
	// TODO: move price-related calculation out from render-function.
	if (!_.isEmpty(currentOrder) && sims) {
		price = getPrice(currentOrder.moisPerSim, currentOrder.dataPerSim);
		dataPerSim = getDataPerSimNumber(currentOrder.dataPerSim);
		moisPerSim = _.has(currentOrder, 'moisPerSim') ? currentOrder.moisPerSim : 0;
		simsCount = sims.length;
		moisTotal = businessProducts.getMoisPackageTotal(moisPerSim, simsCount);
		dataTotal = businessProducts.getDataPackageTotal(dataPerSim, simsCount);
	}
	return (
		<article>
			<Helmet title={getUIString(accountType, 'Dashboard.title')} meta={[{ name: 'description', content: 'Dashboard' }]} />
			<DashboardContent
				getMaxValue={getMaxValue}
				getDataUsage={getDataUsage}
				getMessageAndVoiceUsage={getMessageAndVoiceUsage}
				updateBillingPeriod={updateBillingPeriod}
				getWarningValue={businessProducts.getWarningValue}
				getEstimate={businessProducts.getEstimate}
				isUnlimited={businessProducts.isUnlimited}
				dataTotal={dataTotal}
				moisTotal={moisTotal}
				price={price}
				simsCount={simsCount}
				dataPerSim={dataPerSim}
				moisPerSim={moisPerSim}
				nonActivatedSims={nonActivatedSims}
				accountType={props.accountType}
				billingPeriods={props.billingPeriods}
				activeBillingPeriod={props.activeBillingPeriod}
				errors={props.errors}
				usageFetching={props.usageFetching}
				usage={props.usage}
				currentOrder={props.currentOrder}
				emptySims={props.emptySims}
				sims={props.sims}
				offeringType={props.offeringType}
			/>
		</article>
	);
};

const mapStateToProps = createStructuredSelector({
	billingPeriods: makeSelectBillingPeriods(),
	activeBillingPeriod: makeSelectActiveBillingPeriod(),
	currentOrder: makeSelectCurrentOrder(),
	emptySims: makeSelectEmptySims(),
	errors: selectErrors,
	usageFetching: makeSelectFetchingUsage(),
	nonActivatedSimsFetching: makeSelectNonActivatedSimsFetching(),
	ready: makeSelectReady(),
	usage: makeSelectAllUsages(),
	sims: makeSelectSims(),
	simsfetching: makeSelectFetching(), // TODO: Tarkista käytetäänkö tätä mihinkään? +camelCase-errori
	nonActivatedSims: makeSelectNonActivatedSims()
});

export default connect(mapStateToProps)(DashboardPageImpl);
